import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-form';
import moment from 'moment';
import PropTypes, { any } from 'prop-types';

import { getPlanSummary } from '../../../api/tmsEventsDayPlannerApi';
import { getApiAxiosInstance } from '../../../utils/network';
import DayPlannerSidePanel from './DayPlannerSidePanel';
import { dateFormat, timeFormatForDayConfig } from '../../../utils/courtSchedular';
import { TIME_FORMAT_12H } from '../../../utils/formatting';
import { Select } from '../../../components/Form';
import { updateDayPlannerSummary } from '../../../actions/day-planner-actions';
import { getDateTimeObjectFromCurrentDateAndTime } from '../../../utils/dayPlanner';
import getOptionsForTimeSelect from '../../../components/Form/utils';
import { getLabelByTypeId } from '../../../utils/types';

import styles from './dayPlanner.scss';

const SummarySidePanel = (props) => {
    const {
        className,
        closeSidePanel,
        eventId,
        eventSchedule,
    } = props;

    const dispatch = useDispatch();
    const dayPlanner = useSelector(({ dayPlanner }) => dayPlanner);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { types: { tms: { division: { tournament: { drawEliminationTypes } } } } } = coreUI;

    const [planSummary, setPlanSummary] = useState(null);
    const [totals, setTotals] = useState({});

    const startTimeOptions = useMemo(() => {
        const dayStartTime = dayPlanner?.dayConfig?.startTime;
        const timeSelectOptions = getOptionsForTimeSelect(15);

        if (dayStartTime) {
            const { timeZone } = eventSchedule;
            const startTime = moment.utc(dayStartTime).tz(timeZone?.value).format(timeFormatForDayConfig);
            const startTimeIndex = timeSelectOptions.findIndex((option) => option.value === startTime);
            if (startTimeIndex >= 0) {
                timeSelectOptions.splice(0, startTimeIndex);
            }
        }

        return timeSelectOptions;
    }, [dayPlanner.dayConfig, eventSchedule]);

    const defaultValues = useMemo(() => {
        if (planSummary) {
            return {
                summaryDivisions: planSummary.summaries?.map((summary) => ({
                    ...summary,
                    startTime: moment.utc(summary.plannedStartTime).tz(eventSchedule?.timeZone?.value).format(timeFormatForDayConfig),
                })),
            };
        }

        return null;
    }, [planSummary, eventSchedule]);

    useEffect(() => {
        const getPlanSummaryData = async () => {
            const planSummary = await getPlanSummary(getApiAxiosInstance(), eventId, dayPlanner.viewState.primaryView, dayPlanner.dayConfig.id, dayPlanner.viewState.facilityId);
            let entrants = 0;
            let matches = 0;
            (planSummary.data?.summaries || []).forEach((summary) => {
                entrants += summary.entrants;
                matches += summary.matches;
            });
            setPlanSummary(planSummary.data);
            setTotals({ entrants, matches });
        };

        if (!planSummary) {
            getPlanSummaryData();
        }
    }, [planSummary, eventId, eventSchedule]);

    const onFormSubmit = async (values) => {
        const updatedSummaryDivisions = values.summaryDivisions.filter((sumDiv, i) => {
            return sumDiv.startTime !== defaultValues.summaryDivisions[i].startTime;
        });
        const updatedValues = {
            updates: updatedSummaryDivisions.map((sd) => ({
                virtualDrawId: sd.id,
                startTime: getDateTimeObjectFromCurrentDateAndTime(dayPlanner.dayConfig.day, sd.startTime, eventSchedule),
            })),
        };

        dispatch(updateDayPlannerSummary(updatedValues));
    };

    const onCancelClick = () => {
        closeSidePanel();
    };

    const getDisplayableTime = (time) => {
        if (time) {
            return moment.utc(time).tz(eventSchedule?.timeZone?.value).format(TIME_FORMAT_12H);
        }

        return null;
    };

    const getDisplayableFormat = (eliminationTypeId) => {
        if (eliminationTypeId) {
            return getLabelByTypeId(drawEliminationTypes, eliminationTypeId);
        }
        return null;
    };

    if (!defaultValues) {
        return null;
    }

    return (
        <Form
            onSubmit={onFormSubmit}
            defaultValues={defaultValues}
        >
            {(formApi) => {
                const { submitForm, values } = formApi;

                const anyStartTimeChanged = values.summaryDivisions.map((sumDiv, i) => {
                    return sumDiv.startTime === defaultValues.summaryDivisions[i].startTime;
                }).filter((changed) => !changed).length > 0;

                return (
                    <form onSubmit={submitForm} id="summaryForm" data-testid="day-planner.summary.form">
                        <DayPlannerSidePanel
                            className={className}
                            closeSideBar={closeSidePanel}
                            data-testid="day-planner.summary.panel"
                        >
                            <div className={styles.header} data-testid="day-planner.summary.header">
                                {`Summary ${moment(dayPlanner.viewState?.day).format(dateFormat)}`}
                            </div>
                            <div className={`${styles.content} ${styles.summaryDivisions}`}>
                                <div className={styles.divisionList}>
                                    <div className={`d-flex align-items-center ${styles.divisionListHeader}`}>
                                        <div className={styles.divisions} data-testid="day-planner.summary.col-divisions">
                                            Divisions
                                        </div>
                                        <div className={styles.entrants} data-testid="day-planner.summary.col-format">
                                            Format
                                        </div>
                                        <div className={styles.entrants} data-testid="day-planner.summary.col-entrants">
                                            Players/Teams
                                        </div>
                                        <div className={styles.matches} data-testid="day-planner.summary.col-matches">
                                            Matches
                                        </div>
                                        <div className={styles.courtTimes} data-testid="day-planner.summary.col-court-times">
                                            Ct. time (h)
                                        </div>
                                        <div className={styles.plannedStart} data-testid="day-planner.summary.col-planned-start">
                                            Planned start
                                        </div>
                                        <div className={styles.suggestedStart} data-testid="day-planner.summary.col-suggested-start">
                                            Sug. start
                                        </div>
                                        <div className={styles.startTime} data-testid="day-planner.summary.col-start-time">
                                            Start time
                                        </div>
                                        <div className={styles.lastMatchEndTime} data-testid="day-planner.summary.col-last-match-end">
                                            Last match end
                                        </div>
                                    </div>
                                    {values.summaryDivisions?.map((division, i) => {
                                        const divTestId = `day-planner.summary.division-${division.id}`;
                                        return (
                                            <div
                                                key={`virtual-draw-${division.id}`}
                                                className={`d-flex align-items-center ${styles.divisionListItem}`}
                                                data-testid={divTestId}
                                            >
                                                <div className={styles.divisions} data-testid={`${divTestId}.name`}>
                                                    {division.name}
                                                </div>
                                                <div className={styles.entrants} data-testid={`${divTestId}.format`}>
                                                    {getDisplayableFormat(division.tmsTournamentDrawElimTypeId)}
                                                </div>
                                                <div className={styles.entrants} data-testid={`${divTestId}.entrants`}>
                                                    {division.entrants}
                                                </div>
                                                <div className={styles.matches} data-testid={`${divTestId}.matches`}>
                                                    {division.matches}
                                                </div>
                                                <div className={styles.courtTimes} data-testid={`${divTestId}.court-time`}>
                                                    {division.courtTimeHours}
                                                </div>
                                                <div className={styles.plannedStart} data-testid={`${divTestId}.planned-start`}>
                                                    {getDisplayableTime(division.plannedStartTime)}
                                                </div>
                                                <div className={styles.suggestedStart} data-testid={`${divTestId}.suggested-start`}>
                                                    {getDisplayableTime(division.suggestedStartTime)}
                                                </div>
                                                <Select
                                                    id={`summaryDivisions${i}startTime`}
                                                    field={['summaryDivisions', i, 'startTime']}
                                                    formGroupCssClass={`mb0 ${styles.startTime}`}
                                                    options={startTimeOptions}
                                                    attributeToShow="title"
                                                    attributeToSave="value"
                                                    hiddenDefault
                                                    themeV3
                                                    data-testid={`${divTestId}.start-time-select`}
                                                />
                                                <div className={styles.lastMatchEndTime} data-testid={`${divTestId}.last-match-end`}>
                                                    {getDisplayableTime(division.lastMatchEndTime)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div
                                        className={`d-flex align-items-center ${styles.divisionListItem}`}
                                        data-testid="day-planner.summary.totals-row"
                                    >
                                        <div className={`${styles.divisions} ${styles.total}`} data-testid="day-planner.summary.totals-row.label">
                                            Total
                                        </div>
                                        <div className={`${styles.entrants} ${styles.total}`} data-testid="day-planner.summary.totals-row.entrants">
                                            {totals.entrants}
                                        </div>
                                        <div className={`${styles.matches} ${styles.total}`} data-testid="day-planner.summary.totals-row.matches">
                                            {totals.matches}
                                        </div>
                                        <div className={styles.courtTimes} />
                                        <div className={styles.plannedStart} />
                                        <div className={styles.suggestedStart} />
                                        <div className={styles.startTime} />
                                        <div className={styles.lastMatchEndTime} />
                                    </div>
                                </div>
                            </div>
                            <div className={`${styles.footer} d-flex`}>
                                <button
                                    className={`btn btn-primary ${styles.submitButton} mr8`}
                                    type="button"
                                    onClick={onCancelClick}
                                    data-testid="day-planner.summary.cancel-btn"
                                >
                                    Cancel
                                </button>
                                <div className="flex-1" />
                                <button
                                    className={`btn btn-primary ${styles.submitButton}`}
                                    type="submit"
                                    disabled={!anyStartTimeChanged}
                                    data-testid="day-planner.summary.reschedule-btn"
                                >
                                    Re-schedule
                                </button>
                            </div>
                        </DayPlannerSidePanel>
                    </form>
                );
            }}
        </Form>
    );
};

SummarySidePanel.propTypes = {
    className: PropTypes.string,
    closeSidePanel: PropTypes.func.isRequired,
    eventId: PropTypes.number.isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
};

SummarySidePanel.defaultProps = {
    className: '',
};

export default SummarySidePanel;
